import React, { Component } from 'react'
import { Button, Form, FormInstance, Input, Select, Spin } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { ExternalOption } from '../ShelterBuddyExternalField'

/** Searches external options for a given integration and term. Should throw on failure. */
export type SearchExternalList = (integrationUuid: string, term: string) => Promise<ExternalOption[]>

type Props = {
  form: FormInstance
  integrationUuid: string
  /** Resolves the external options matching a typed search term (server-side search). */
  searchExternalList: SearchExternalList
  /** Label for the external Form.Item. Defaults to "External". */
  label?: string
  /** Placeholder for the external Select. Defaults to "Type to search…". */
  placeholder?: string
  /** Form field that stores the selected option's id. Defaults to "external_id". */
  name?: string
  /** Form field that stores the selected option's display name. Pass null to skip storing it. Defaults to "name". */
  nameField?: string | null
  /** Minimum characters before a search fires. Defaults to 2. */
  minSearchLength?: number
}

type State = {
  fetching: boolean
  options: ExternalOption[] | undefined
  error: string | undefined
}

const SEARCH_DEBOUNCE_MS = 400

/**
 * Shelter Buddy external picker backed by a *server-side search* instead of a
 * preloaded list. Useful when the option set is too large to list (e.g. breeds):
 * the user types a term, which is debounced and passed to `searchExternalList`.
 * Stores the chosen option's id in `name` (default "external_id") and its display
 * name in `nameField` (default "name").
 */
export default class ShelterBuddySearchField extends Component<Props, State> {
  state: State = { fetching: false, options: undefined, error: undefined }

  private debounceTimer: any = null
  /** The latest term the user typed — used to drop superseded responses. */
  private lastTerm = ''

  componentWillUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  handleSearch = (term: string) => {
    const { minSearchLength = 2 } = this.props
    this.lastTerm = term

    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    const trimmed = term.trim()
    if (trimmed.length < minSearchLength) {
      this.setState({ options: undefined, error: undefined, fetching: false })
      return
    }

    // Reflect the pending search immediately so the spinner reacts on the first
    // keystroke instead of waiting out the debounce — otherwise the component
    // looks unresponsive while the timer runs.
    this.setState({ fetching: true, error: undefined })
    this.debounceTimer = setTimeout(() => this.runSearch(trimmed), SEARCH_DEBOUNCE_MS)
  }

  runSearch = (term: string) => {
    const { integrationUuid, searchExternalList } = this.props
    if (!integrationUuid) return

    this.setState({ fetching: true, error: undefined }, async () => {
      try {
        const options = await searchExternalList(integrationUuid, term)
        // A newer keystroke may have superseded this request — ignore stale results.
        if (this.lastTerm.trim() !== term) return
        this.setState({ fetching: false, options, error: undefined })
      } catch (e) {
        console.error(e)
        const message = (e as Error)?.message || 'Failed to search external list'
        this.setState({ fetching: false, options: undefined, error: message })
      }
    })
  }

  handleRetry = () => {
    const trimmed = this.lastTerm.trim()
    if (trimmed) this.runSearch(trimmed)
  }

  handleClear = () => {
    const { form, name = 'external_id', nameField = 'name' } = this.props
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.lastTerm = ''
    const updates: Record<string, undefined> = { [name]: undefined }
    if (nameField) updates[nameField] = undefined
    form.setFieldsValue(updates)
    this.setState({ fetching: false, options: undefined, error: undefined })
  }

  render() {
    const {
      form,
      label = 'External',
      placeholder = 'Type to search…',
      name = 'external_id',
      nameField = 'name',
    } = this.props
    const { fetching, options, error } = this.state

    // Keep the saved value readable before/without a search (e.g. on edit) by
    // seeding a single option from the current form values.
    const currentValue = form.getFieldValue(name)?.toString()
    const currentName = nameField ? form.getFieldValue(nameField) : undefined
    const fallbackOptions: ExternalOption[] | undefined =
      currentValue
        ? [{ value: currentValue, name: currentName ?? '', label: currentName ? `${currentName} [${currentValue}]` : currentValue }]
        : undefined

    // While a search is running, clear the list so the seeded/previous options
    // don't linger as if they were results — the spinner in `notFoundContent`
    // signals progress instead. Once a search has run (`options` defined) show
    // its results; before any search, seed the saved value so it stays readable.
    const selectOptions = fetching ? [] : options ?? fallbackOptions

    const notFoundContent = fetching ? (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <Spin size="small" /> <span style={{ marginLeft: 8 }}>Searching…</span>
      </div>
    ) : options !== undefined ? (
      // A search completed with no matches.
      'No entries found'
    ) : (
      'Type to search the external list.'
    )

    const help = error ? (
      <span>
        {error}{' '}
        <Button
          size="small"
          type="link"
          icon={<ReloadOutlined />}
          loading={fetching}
          onClick={this.handleRetry}
        >
          Retry
        </Button>
      </span>
    ) : 'Type to search the external list.'

    return (
      <>
        <Form.Item
          label={label}
          name={name}
          validateStatus={error ? 'error' : undefined}
          help={help}
          rules={[{ required: true, message: 'Missing external mapping' }]}
        >
          <Select
            loading={fetching}
            placeholder={placeholder}
            options={selectOptions}
            showSearch
            allowClear
            // Server-side search — don't also filter locally.
            filterOption={false}
            onSearch={this.handleSearch}
            notFoundContent={notFoundContent}
            onClear={this.handleClear}
            onSelect={(_value, option) => {
              const picked = option as unknown as ExternalOption
              if (nameField) {
                form.setFieldsValue({ [nameField]: picked.name })
              }
            }}
          />
        </Form.Item>
        {nameField ? (
          <Form.Item
            hidden
            name={nameField}
            rules={[{ required: true, message: 'Missing external mapping' }]}
          >
            <Input />
          </Form.Item>
        ) : null}
      </>
    )
  }
}
